// THE filter button (Free, the price, the types; web/controls.js filterChip, ios FilterChip): one look, off and on
// (nowish.json chipOn), so they can't drift. An optional leading icon; its label is text, emojis (as many as fit,
// then "…"), or text with an icon after it ("All ★"). Every icon sits on the middle of the text's capital letters,
// lifted by nowish.json iconInkDrop when its drawing sits low in its box.
import { forwardRef, useLayoutEffect, useRef, type ButtonHTMLAttributes, type CSSProperties } from "react";
import { SHARED, T } from "../../shared/config";
import { Icon, type IconName } from "../../shared/icons";
import { observeResize } from "../../shared/resize";
import styles from "./FilterChip.module.css";

// An on button's colours (nowish.json chipOn: colour token names) as CSS variables.
const tokenVar = (t: string) => `var(--${t.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)})`;
const CHIP_ON = Object.fromEntries(Object.entries(SHARED.chipOn).map(([part, token]) => [`--chip-on-${part}`, tokenVar(token)])) as CSSProperties;
const INK_DROP = SHARED.iconInkDrop as Record<string, number>;
const ChipIcon = ({ name, className }: { name: IconName; className?: string }) => (
  <span className={className ? `${styles.ink} ${className}` : styles.ink} style={{ "--ink-drop": INK_DROP[name] ?? 0 } as CSSProperties}>
    <Icon name={name} />
  </span>
);

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  on: boolean;
  lead?: IconName | null;
  label?: string | null;
  labelIcon?: IconName | null;
  emojis?: string[] | null;
  dropdown?: boolean;
}

export const FilterChip = forwardRef<HTMLButtonElement, Props>(function FilterChip(
  { on, lead, label, labelIcon, emojis, dropdown = false, className, ...rest }, ref) {
  const cls = [styles.chip, dropdown && styles.dropdown, on && styles.on, className].filter(Boolean).join(" ");
  return (
    <button ref={ref} type="button" className={cls} style={CHIP_ON} {...rest}>
      {lead && <ChipIcon name={lead} className={styles.lead} />}
      {emojis ? <FitEmojis emojis={emojis} /> : <span className={styles.label}>{label}</span>}
      {labelIcon && <ChipIcon name={labelIcon} />}
      {dropdown && <ChipIcon name="chevron-down" className={styles.chevron} />}
    </button>
  );
});

// As many emojis as the button's real width holds; "…" after them only when not all fit.
function FitEmojis({ emojis }: { emojis: string[] }) {
  const el = useRef<HTMLSpanElement>(null), key = emojis.join("");
  useLayoutEffect(() => {
    const span = el.current;
    if (!span) return;
    const fit = () => {
      span.textContent = emojis.join("");
      for (let n = emojis.length - 1; n >= 0 && span.scrollWidth > span.clientWidth; n--) span.textContent = `${emojis.slice(0, n).join("")}…`;
    };
    fit();
    return observeResize(span.parentElement!, fit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return <span ref={el} className={`${styles.label} ${styles.emojis}`} />;
}

/** THE Reset pill (under the price slider; floating over the types list): one look, the undo icon (nowish.json resetIcon). */
export function ResetPill({ shown, floating = false, id, onClick }: { shown: boolean; floating?: boolean; id: string; onClick: () => void }) {
  const cls = [styles.chip, styles.small, styles.reset, shown && styles.shown, floating && styles.floating].filter(Boolean).join(" ");
  return (
    <button type="button" id={id} className={cls} onClick={onClick} tabIndex={shown ? 0 : -1} aria-hidden={!shown}>
      <ChipIcon name={SHARED.resetIcon as IconName} />{T("filter.reset")}
    </button>
  );
}
